import type { ReactNode } from 'react'
import React, { useMemo, createContext, useContext, useEffect, useRef } from 'react'
import { useLocation } from '@fs/zion-router'
import { matchPath } from 'react-router'
import { usePersonPageParams } from '../person-page/personPageRouteUtil'
import { useNotes } from '../data-service/useNotes'
import type { Note } from '../data-service/notes'
import useSourceCentricDetailsFlag from '../data-service/useSourceCentricDetailsFlag'
import { NOTES_ROUTE_PATH } from '../notes/notesRouteUtil'

type value = {
  notes: Note[]
  loading: boolean
}

const PersonNotesContext = createContext<value>({ notes: [], loading: true })

export const usePersonNotes = (): value => useContext(PersonNotesContext)

export default function PersonNotesProvider({ children }: { children: ReactNode }): JSX.Element {
  const { pid, subPage } = usePersonPageParams('full')
  const isFirstRender = useRef(true)
  const { pathname } = useLocation()
  const isNotesPage = !!matchPath(pathname, { path: NOTES_ROUTE_PATH })
  const isSourceCentric = useSourceCentricDetailsFlag().data?.isOn

  let numberOfNoteDisplayed = 0
  if (subPage === 'details')
    numberOfNoteDisplayed = 3 // for MiniNotes
  else if (subPage === 'family' || subPage === 'non-vitals' || subPage === 'vitals')
    numberOfNoteDisplayed = 1 // for AlertNoteBanner
  else if (subPage === 'collaborate' || isNotesPage) numberOfNoteDisplayed = Infinity
  else if (isSourceCentric) numberOfNoteDisplayed = 1 // minimum to populate count in tools menu

  const { notes, isLoading, mutate } = useNotes('person', pid, numberOfNoteDisplayed)

  const value = useMemo(() => ({ notes: notes ?? [], loading: isLoading }), [isLoading, notes])

  useEffect(() => {
    if (subPage === 'details' || subPage === 'collaborate') {
      !isFirstRender.current && mutate()
    }
    isFirstRender.current = false
  }, [mutate, subPage])

  return <PersonNotesContext.Provider value={value}>{children}</PersonNotesContext.Provider>
}
